/**
 * FinanceManager - Comprehensive finance management for events
 * Handles funds, expenses, and stock/inventory tracking
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { financeAPI, getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { formatCurrency as fmtCurrency } from '@/lib/currency';
import {
  Plus, Trash2, Edit2, DollarSign, TrendingUp, TrendingDown, Package,
  Wallet, CreditCard, Receipt, BarChart3, AlertTriangle, Loader2, Save,
  ArrowUpRight, ArrowDownRight, Box, ShoppingCart, RefreshCw
} from 'lucide-react';

const FUND_TYPES = [
  { value: 'sponsorship', label: 'Sponsorship' },
  { value: 'ticket_sales', label: 'Ticket Sales' },
  { value: 'donation', label: 'Donation' },
  { value: 'grant', label: 'Grant' },
  { value: 'registration', label: 'Registration Fees' },
  { value: 'other', label: 'Other' },
];

const EXPENSE_CATEGORIES = [
  { value: 'venue', label: 'Venue' },
  { value: 'catering', label: 'Catering' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'staff', label: 'Staff/Labor' },
  { value: 'travel', label: 'Travel' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'printing', label: 'Printing' },
  { value: 'technology', label: 'Technology' },
  { value: 'other', label: 'Other' },
];

const STOCK_CATEGORIES = [
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'promotional', label: 'Promotional Items' },
  { value: 'other', label: 'Other' },
];

const FinanceManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showFundDialog, setShowFundDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [showPayerDialog, setShowPayerDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [selectedPayer, setSelectedPayer] = useState(null);

  // Fetch summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['finance-summary', eventId],
    queryFn: () => financeAPI.getSummary(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Fetch funds
  const { data: funds = [] } = useQuery({
    queryKey: ['finance-funds', eventId],
    queryFn: () => financeAPI.listFunds(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Fetch income
  const { data: incomeEntries = [] } = useQuery({
    queryKey: ['finance-income', eventId],
    queryFn: () => financeAPI.listIncome(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Fetch payers
  const { data: payers = [] } = useQuery({
    queryKey: ['finance-payers', eventId],
    queryFn: () => financeAPI.listPayers(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Fetch expenses
  const { data: expenses = [] } = useQuery({
    queryKey: ['finance-expenses', eventId],
    queryFn: () => financeAPI.listExpenses(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Fetch stock
  const { data: stockItems = [] } = useQuery({
    queryKey: ['finance-stock', eventId],
    queryFn: () => financeAPI.listStock(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Income mutations
  const createIncomeMutation = useMutation({
    mutationFn: (data) => financeAPI.createIncome(eventId, data),
    onSuccess: () => {
      toast.success('Income recorded successfully');
      queryClient.invalidateQueries(['finance-income', eventId]);
      queryClient.invalidateQueries(['finance-payers', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      setShowIncomeDialog(false);
      setEditingItem(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to record income')),
  });

  const updateIncomeMutation = useMutation({
    mutationFn: ({ id, data }) => financeAPI.updateIncome(eventId, id, data),
    onSuccess: () => {
      toast.success('Income updated');
      queryClient.invalidateQueries(['finance-income', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      setShowIncomeDialog(false);
      setEditingItem(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update income')),
  });

  const deleteIncomeMutation = useMutation({
    mutationFn: (incomeId) => financeAPI.deleteIncome(eventId, incomeId),
    onSuccess: () => {
      toast.success('Income entry deleted');
      queryClient.invalidateQueries(['finance-income', eventId]);
      queryClient.invalidateQueries(['finance-payers', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
    },
  });

  // Payer mutations
  const createPayerMutation = useMutation({
    mutationFn: (data) => financeAPI.createPayer(eventId, data),
    onSuccess: () => {
      toast.success('Payer added');
      queryClient.invalidateQueries(['finance-payers', eventId]);
      setShowPayerDialog(false);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to add payer')),
  });

  // Fund mutations
  const createFundMutation = useMutation({
    mutationFn: (data) => financeAPI.createFund(eventId, data),
    onSuccess: () => {
      toast.success('Fund added successfully');
      queryClient.invalidateQueries(['finance-funds', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      setShowFundDialog(false);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to add fund')),
  });

  const deleteFundMutation = useMutation({
    mutationFn: (fundId) => financeAPI.deleteFund(eventId, fundId),
    onSuccess: () => {
      toast.success('Fund deleted');
      queryClient.invalidateQueries(['finance-funds', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
    },
  });

  // Expense mutations
  const createExpenseMutation = useMutation({
    mutationFn: (data) => financeAPI.createExpense(eventId, data),
    onSuccess: () => {
      toast.success('Expense recorded');
      queryClient.invalidateQueries(['finance-expenses', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      setShowExpenseDialog(false);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to record expense')),
  });

  const updateExpenseMutation = useMutation({
    mutationFn: ({ id, data }) => financeAPI.updateExpense(eventId, id, data),
    onSuccess: () => {
      toast.success('Expense updated');
      queryClient.invalidateQueries(['finance-expenses', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      setShowExpenseDialog(false);
      setEditingItem(null);
    },
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: (expenseId) => financeAPI.deleteExpense(eventId, expenseId),
    onSuccess: () => {
      toast.success('Expense deleted');
      queryClient.invalidateQueries(['finance-expenses', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
    },
  });

  // Approval mutations — flip pending ↔ approved so it counts toward Revenue.
  const approveIncomeMutation = useMutation({
    mutationFn: (id) => financeAPI.approveIncome(eventId, id),
    onSuccess: () => {
      toast.success('Income approved');
      queryClient.invalidateQueries(['finance-income', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      queryClient.invalidateQueries(['event-finance-summary', eventId]);
      queryClient.invalidateQueries(['revenue-event-finance']);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to approve income')),
  });
  const unapproveIncomeMutation = useMutation({
    mutationFn: (id) => financeAPI.unapproveIncome(eventId, id),
    onSuccess: () => {
      toast.success('Income moved to pending');
      queryClient.invalidateQueries(['finance-income', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      queryClient.invalidateQueries(['event-finance-summary', eventId]);
      queryClient.invalidateQueries(['revenue-event-finance']);
    },
  });
  const approveExpenseMutation = useMutation({
    mutationFn: (id) => financeAPI.approveExpense(eventId, id),
    onSuccess: () => {
      toast.success('Expense approved');
      queryClient.invalidateQueries(['finance-expenses', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      queryClient.invalidateQueries(['event-finance-summary', eventId]);
      queryClient.invalidateQueries(['revenue-event-finance']);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to approve expense')),
  });
  const unapproveExpenseMutation = useMutation({
    mutationFn: (id) => financeAPI.unapproveExpense(eventId, id),
    onSuccess: () => {
      toast.success('Expense moved to pending');
      queryClient.invalidateQueries(['finance-expenses', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      queryClient.invalidateQueries(['event-finance-summary', eventId]);
      queryClient.invalidateQueries(['revenue-event-finance']);
    },
  });

  // Stock mutations
  const createStockMutation = useMutation({
    mutationFn: (data) => financeAPI.createStockItem(eventId, data),
    onSuccess: () => {
      toast.success('Stock item added');
      queryClient.invalidateQueries(['finance-stock', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      setShowStockDialog(false);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to add stock item')),
  });

  const stockTransactionMutation = useMutation({
    mutationFn: ({ itemId, data }) => financeAPI.recordStockTransaction(eventId, itemId, data),
    onSuccess: () => {
      toast.success('Stock transaction recorded');
      queryClient.invalidateQueries(['finance-stock', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
      setShowTransactionDialog(false);
      setSelectedStockItem(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to record transaction')),
  });

  const deleteStockMutation = useMutation({
    mutationFn: (itemId) => financeAPI.deleteStockItem(eventId, itemId),
    onSuccess: () => {
      toast.success('Stock item deleted');
      queryClient.invalidateQueries(['finance-stock', eventId]);
      queryClient.invalidateQueries(['finance-summary', eventId]);
    },
  });

  const formatCurrency = (amount, cur) => fmtCurrency(amount, cur || 'USD');

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="income">Income</TabsTrigger>
          <TabsTrigger value="payers">Payers</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="funds">Funds</TabsTrigger>
          <TabsTrigger value="stock">Stock</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card data-testid="finance-summary-income">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Income ({summary?.display_currency || 'USD'})</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="finance-total-income">
                      {formatCurrency(summary?.total_income, summary?.display_currency)}
                    </p>
                    {summary?.pending_income > 0 && (
                      <p className="text-xs text-amber-600 mt-1">{summary.pending_income} pending approval</p>
                    )}
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="finance-summary-expenses">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Expenses ({summary?.display_currency || 'USD'})</p>
                    <p className="text-2xl font-bold text-red-600" data-testid="finance-total-expenses">
                      {formatCurrency(summary?.total_expenses, summary?.display_currency)}
                    </p>
                    {summary?.pending_expenses > 0 && (
                      <p className="text-xs text-amber-600 mt-1">{summary.pending_expenses} pending approval</p>
                    )}
                  </div>
                  <div className="p-3 bg-red-100 rounded-full">
                    <TrendingDown className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="finance-summary-net">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Net Revenue ({summary?.display_currency || 'USD'})</p>
                    <p className={`text-2xl font-bold ${(summary?.net_balance ?? 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`} data-testid="finance-net-revenue">
                      {formatCurrency(summary?.net_balance, summary?.display_currency)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${(summary?.net_balance ?? 0) >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                    <Wallet className={`h-6 w-6 ${(summary?.net_balance ?? 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="finance-summary-donations">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Donations ({summary?.display_currency || 'USD'})</p>
                    <p className="text-2xl font-bold text-purple-600" data-testid="finance-total-donations">
                      {formatCurrency(summary?.donations_totals_by_currency?.[summary?.display_currency || 'USD'], summary?.display_currency)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">approved only</p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <Package className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Budget Progress */}
          {summary?.funds?.total > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Budget Utilization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Spent: {formatCurrency(summary?.expenses?.total)}</span>
                    <span>Budget: {formatCurrency(summary?.funds?.total)}</span>
                  </div>
                  <Progress 
                    value={Math.min((summary?.expenses?.total / summary?.funds?.total) * 100, 100)} 
                    className="h-3"
                  />
                  <p className="text-xs text-slate-500">
                    {((summary?.expenses?.total / summary?.funds?.total) * 100).toFixed(1)}% of budget used
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Low Stock Alert */}
          {summary?.stock?.low_stock_count > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  Low Stock Alert
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-amber-700 mb-2">
                  {summary.stock.low_stock_count} items are running low:
                </p>
                <div className="flex flex-wrap gap-2">
                  {summary.stock.low_stock_items?.map((item) => (
                    <Badge key={item.id} variant="outline" className="border-amber-400 text-amber-700">
                      {item.name}: {item.quantity_available} left
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Income Tab */}
        <TabsContent value="income" className="space-y-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-3">
              <p className="text-sm text-green-800">
                <strong>💰 Income</strong> — Record all money received for this event: ticket sales, registration fees, 
                one-time donations, sponsorship payments, etc. This is the actual money that has come in.
              </p>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Income / Donations</h3>
            <Button onClick={() => { setEditingItem(null); setShowIncomeDialog(true); }} data-testid="add-income-btn">
              <Plus className="h-4 w-4 mr-2" />
              Record Income
            </Button>
          </div>

          <div className="space-y-3">
            {incomeEntries.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  No income recorded yet. Add your first payment or donation.
                </CardContent>
              </Card>
            ) : (
              incomeEntries.map((income) => {
                const isApproved = (income.status || 'approved') === 'approved';
                return (
                <Card key={income.id} className={`hover:bg-slate-50 transition-colors ${isApproved ? '' : 'border-amber-200 bg-amber-50/30'}`} data-testid={`income-row-${income.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <ArrowUpRight className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{income.name}</p>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                            <span className="font-medium text-slate-700">{income.payer_name}</span>
                            <Badge variant="outline" className="text-xs">{income.category}</Badge>
                            <Badge
                              variant="outline"
                              className={`text-xs ${isApproved ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-amber-400 text-amber-700 bg-amber-50'}`}
                              data-testid={`income-status-${income.id}`}
                            >
                              {isApproved ? '✓ Approved' : 'Pending'}
                            </Badge>
                            {income.currency && income.currency !== 'USD' && (
                              <Badge variant="secondary" className="text-xs">{income.currency}</Badge>
                            )}
                            {income.receipt_number && (
                              <Badge variant="secondary" className="text-xs">
                                <Receipt className="h-3 w-3 mr-1" />
                                {income.receipt_number}
                              </Badge>
                            )}
                            {income.date_received && (
                              <span className="text-xs">{new Date(income.date_received).toLocaleDateString()}</span>
                            )}
                          </div>
                          {income.payment_method && (
                            <span className="text-xs text-slate-400">via {income.payment_method}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-lg font-semibold text-green-600">
                          +{formatCurrency(income.amount, income.currency)}
                        </p>
                        <div className="flex gap-1">
                          {isApproved ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => unapproveIncomeMutation.mutate(income.id)}
                              disabled={unapproveIncomeMutation.isPending}
                              data-testid={`unapprove-income-${income.id}`}
                            >
                              Unapprove
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => approveIncomeMutation.mutate(income.id)}
                              disabled={approveIncomeMutation.isPending}
                              data-testid={`approve-income-${income.id}`}
                            >
                              Approve
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setEditingItem(income); setShowIncomeDialog(true); }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => confirm('Delete this income entry?') && deleteIncomeMutation.mutate(income.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Payers/Donors Tab */}
        <TabsContent value="payers" className="space-y-4">
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="py-3">
              <p className="text-sm text-purple-800">
                <strong>👥 Payers & Donors</strong> — Manage your contact database of people and organizations who pay 
                or donate. Add sponsors, donors, companies, or individuals here so you can track who has contributed to your event.
              </p>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Payers & Donors</h3>
            <Button onClick={() => setShowPayerDialog(true)} data-testid="add-payer-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Payer
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {payers.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-slate-500">
                  No payers recorded yet. Payers are automatically created when you record income.
                </CardContent>
              </Card>
            ) : (
              payers.map((payer) => (
                <Card key={payer.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedPayer(payer)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold">{payer.name}</p>
                        <Badge variant="outline" className="text-xs mt-1">
                          {payer.type === 'company' ? 'Company' : payer.type === 'organization' ? 'Organization' : payer.type === 'individual' ? 'Individual' : payer.type || 'Individual'}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">{formatCurrency(payer.total_paid)}</p>
                        <p className="text-xs text-slate-500">Total Paid</p>
                      </div>
                    </div>
                    {payer.email && <p className="text-sm text-slate-500">{payer.email}</p>}
                    {payer.phone && <p className="text-sm text-slate-500">{payer.phone}</p>}
                    <div className="mt-3 pt-3 border-t flex justify-between text-xs text-slate-400">
                      <span>{payer.payment_count || 0} payments</span>
                      {payer.last_payment_date && (
                        <span>Last: {new Date(payer.last_payment_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Funds Tab */}
        <TabsContent value="funds" className="space-y-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-3">
              <p className="text-sm text-blue-800">
                <strong>🏦 Funds</strong> — Track your funding sources and budgets. Unlike Income (which tracks individual payments received), 
                Funds represent your overall funding categories: sponsorship packages, grants, ticket revenue pools, etc. 
                Use this for budget planning and tracking how much funding you have from each source.
              </p>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Funding Sources & Budgets</h3>
            <Button onClick={() => setShowFundDialog(true)} data-testid="add-fund-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Fund
            </Button>
          </div>

          <div className="space-y-3">
            {funds.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  No funds recorded yet. Add your first income source.
                </CardContent>
              </Card>
            ) : (
              funds.map((fund) => (
                <Card key={fund.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <ArrowUpRight className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{fund.name}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Badge variant="outline">{FUND_TYPES.find(t => t.value === fund.fund_type)?.label || fund.fund_type}</Badge>
                            {fund.source && <span>from {fund.source}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-lg font-semibold text-green-600">+{formatCurrency(fund.amount)}</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => confirm('Delete this fund?') && deleteFundMutation.mutate(fund.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="py-3">
              <p className="text-sm text-red-800">
                <strong>📤 Expenses</strong> — Record all money spent on the event: venue rental, catering, equipment, 
                marketing, staff costs, etc. Keep track of where your budget is going.
              </p>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Expenses</h3>
            <Button onClick={() => { setEditingItem(null); setShowExpenseDialog(true); }} data-testid="add-expense-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Button>
          </div>

          <div className="space-y-3">
            {expenses.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  No expenses recorded yet.
                </CardContent>
              </Card>
            ) : (
              expenses.map((expense) => {
                // Legacy rows without status: infer from payment_status
                const inferredStatus = expense.status || (expense.payment_status === 'paid' ? 'approved' : 'pending');
                const isApproved = inferredStatus === 'approved';
                return (
                <Card key={expense.id} className={isApproved ? '' : 'border-amber-200 bg-amber-50/30'} data-testid={`expense-row-${expense.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <ArrowDownRight className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium">{expense.name}</p>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                            <Badge variant="outline">{EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}</Badge>
                            {expense.vendor && <span>• {expense.vendor}</span>}
                            <Badge variant={expense.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {expense.payment_status}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={`text-xs ${isApproved ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-amber-400 text-amber-700 bg-amber-50'}`}
                              data-testid={`expense-status-${expense.id}`}
                            >
                              {isApproved ? '✓ Approved' : 'Pending'}
                            </Badge>
                            {expense.currency && expense.currency !== 'USD' && (
                              <Badge variant="secondary" className="text-xs">{expense.currency}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-lg font-semibold text-red-600">-{formatCurrency(expense.amount, expense.currency)}</p>
                        {isApproved ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => unapproveExpenseMutation.mutate(expense.id)}
                            disabled={unapproveExpenseMutation.isPending}
                            data-testid={`unapprove-expense-${expense.id}`}
                          >
                            Unapprove
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => approveExpenseMutation.mutate(expense.id)}
                            disabled={approveExpenseMutation.isPending}
                            data-testid={`approve-expense-${expense.id}`}
                          >
                            Approve
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setEditingItem(expense); setShowExpenseDialog(true); }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => confirm('Delete this expense?') && deleteExpenseMutation.mutate(expense.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock" className="space-y-4">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-3">
              <p className="text-sm text-amber-800">
                <strong>📦 Stock & Inventory</strong> — Track physical items for your event: merchandise, supplies, 
                equipment, food & beverage, promotional materials. Monitor quantities, costs, and get alerts when stock is low.
              </p>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Stock & Inventory</h3>
            <Button onClick={() => setShowStockDialog(true)} data-testid="add-stock-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stockItems.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-slate-500">
                  No stock items yet. Add items to track inventory.
                </CardContent>
              </Card>
            ) : (
              stockItems.map((item) => {
                const usagePercent = item.quantity_purchased > 0 
                  ? ((item.quantity_purchased - item.quantity_available) / item.quantity_purchased) * 100 
                  : 0;
                const isLow = item.quantity_available <= item.reorder_level;
                
                return (
                  <Card key={item.id} className={isLow ? 'border-amber-300' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <Badge variant="outline" className="text-xs">
                            {STOCK_CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                          </Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => { setSelectedStockItem(item); setShowTransactionDialog(true); }}
                            title="Record usage"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500"
                            onClick={() => confirm('Delete this item?') && deleteStockMutation.mutate(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className={isLow ? 'text-amber-600 font-medium' : ''}>
                            Available: {item.quantity_available}
                          </span>
                          <span className="text-slate-500">of {item.quantity_purchased}</span>
                        </div>
                        <Progress value={usagePercent} className="h-2" />
                        <p className="text-xs text-slate-500">
                          {item.quantity_used || 0} used • {formatCurrency(item.unit_cost)} each
                        </p>
                      </div>
                      
                      {isLow && (
                        <div className="mt-2 flex items-center gap-1 text-amber-600 text-xs">
                          <AlertTriangle className="h-3 w-3" />
                          Low stock - reorder soon
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Fund Dialog */}
      <FundDialog
        open={showFundDialog}
        onOpenChange={setShowFundDialog}
        onSubmit={(data) => createFundMutation.mutate(data)}
        isLoading={createFundMutation.isPending}
      />

      {/* Add/Edit Expense Dialog */}
      <ExpenseDialog
        open={showExpenseDialog}
        onOpenChange={(open) => { setShowExpenseDialog(open); if (!open) setEditingItem(null); }}
        expense={editingItem}
        onSubmit={(data) => {
          if (editingItem) {
            updateExpenseMutation.mutate({ id: editingItem.id, data });
          } else {
            createExpenseMutation.mutate(data);
          }
        }}
        isLoading={createExpenseMutation.isPending || updateExpenseMutation.isPending}
      />

      {/* Add Stock Dialog */}
      <StockDialog
        open={showStockDialog}
        onOpenChange={setShowStockDialog}
        onSubmit={(data) => createStockMutation.mutate(data)}
        isLoading={createStockMutation.isPending}
      />

      {/* Stock Transaction Dialog */}
      <StockTransactionDialog
        open={showTransactionDialog}
        onOpenChange={(open) => { setShowTransactionDialog(open); if (!open) setSelectedStockItem(null); }}
        item={selectedStockItem}
        onSubmit={(data) => stockTransactionMutation.mutate({ itemId: selectedStockItem?.id, data })}
        isLoading={stockTransactionMutation.isPending}
      />

      {/* Income Dialog */}
      <IncomeDialog
        open={showIncomeDialog}
        onOpenChange={(open) => { setShowIncomeDialog(open); if (!open) setEditingItem(null); }}
        income={editingItem}
        payers={payers}
        onSubmit={(data) => {
          if (editingItem) {
            updateIncomeMutation.mutate({ id: editingItem.id, data });
          } else {
            createIncomeMutation.mutate(data);
          }
        }}
        isLoading={createIncomeMutation.isPending || updateIncomeMutation.isPending}
      />

      {/* Payer Dialog */}
      <PayerDialog
        open={showPayerDialog}
        onOpenChange={setShowPayerDialog}
        onSubmit={(data) => createPayerMutation.mutate(data)}
        isLoading={createPayerMutation.isPending}
      />

      {/* Payer Details Dialog */}
      {selectedPayer && (
        <PayerDetailsDialog
          open={!!selectedPayer}
          onOpenChange={() => setSelectedPayer(null)}
          payer={selectedPayer}
          eventId={eventId}
        />
      )}
    </div>
  );
};

// Fund Dialog Component
const FundDialog = ({ open, onOpenChange, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '', fund_type: 'sponsorship', amount: '', source: '', reference: '', notes: ''
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    onSubmit({ ...formData, amount: parseFloat(formData.amount) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Fund / Income</DialogTitle>
          <DialogDescription>Record a new income source for this event</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Sponsorship from Company X" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={formData.fund_type} onValueChange={(v) => setFormData({ ...formData, fund_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FUND_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Source</Label>
              <Input value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} placeholder="Who provided funds" />
            </div>
            <div className="space-y-2">
              <Label>Reference #</Label>
              <Input value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} placeholder="Invoice/receipt number" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional details..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Add Fund
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Expense Dialog Component
// Full ISO-4217 list — pulled lazily from /api/currency/list on first render
// so admins get every currency the ecosystem accepts (USD/GBP/EUR/ZAR/GHS/…).
// Falls back to a short built-in list if the network call fails.
const FALLBACK_CURRENCY_CODES = ['USD', 'GBP', 'EUR', 'ZAR', 'KES', 'NGN', 'GHS', 'CAD', 'AUD', 'INR', 'ZIG'];

const useCurrencyCodes = () => {
  const [codes, setCodes] = React.useState(FALLBACK_CURRENCY_CODES);
  React.useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/currency/list`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d?.currencies) return;
        setCodes(d.currencies.map(c => c.code));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return codes;
};

const CURRENCY_CODES = FALLBACK_CURRENCY_CODES;

const ExpenseDialog = ({ open, onOpenChange, expense, onSubmit, isLoading }) => {
  const currencyCodes = useCurrencyCodes();
  const [formData, setFormData] = useState({
    name: '', category: 'other', amount: '', currency: 'USD', vendor: '', invoice_number: '', payment_status: 'pending', payment_method: '', notes: ''
  });

  React.useEffect(() => {
    if (expense) {
      setFormData({
        name: expense.name || '',
        category: expense.category || 'other',
        amount: expense.amount?.toString() || '',
        currency: (expense.currency || 'USD').toUpperCase(),
        vendor: expense.vendor || '',
        invoice_number: expense.invoice_number || '',
        payment_status: expense.payment_status || 'pending',
        payment_method: expense.payment_method || '',
        notes: expense.notes || '',
      });
    } else {
      setFormData({ name: '', category: 'other', amount: '', currency: 'USD', vendor: '', invoice_number: '', payment_status: 'pending', payment_method: '', notes: '' });
    }
  }, [expense, open]);

  const handleSubmit = () => {
    if (!formData.name || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    onSubmit({ ...formData, amount: parseFloat(formData.amount) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{expense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
          <DialogDescription>Record an expense for this event</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Venue Rental" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" data-testid="expense-amount-input" />
            </div>
            <div className="space-y-2">
              <Label>Currency *</Label>
              <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                <SelectTrigger data-testid="expense-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {currencyCodes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Input value={formData.vendor} onChange={(e) => setFormData({ ...formData, vendor: e.target.value })} placeholder="Vendor name" />
            </div>
            <div className="space-y-2">
              <Label>Invoice #</Label>
              <Input value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} placeholder="Invoice number" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.payment_status} onValueChange={(v) => setFormData({ ...formData, payment_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="online">Online Payment</SelectItem>
                  <SelectItem value="mobile">Mobile Payment</SelectItem>
                  <SelectItem value="custom">Custom/Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional details..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {expense ? 'Update' : 'Add'} Expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Stock Dialog Component
const StockDialog = ({ open, onOpenChange, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '', category: 'supplies', quantity: '', unit_cost: '', supplier: '', min_quantity: '10', location: '', description: ''
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.quantity) {
      toast.error('Please fill in required fields');
      return;
    }
    const qty = parseInt(formData.quantity);
    onSubmit({
      name: formData.name,
      category: formData.category,
      quantity: qty,
      unit_cost: parseFloat(formData.unit_cost) || 0,
      min_quantity: parseInt(formData.min_quantity) || 10,
      supplier: formData.supplier,
      location: formData.location,
      description: formData.description,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Stock Item</DialogTitle>
          <DialogDescription>Add an item to track in inventory</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Item Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., T-Shirts (Large)" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STOCK_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantity *</Label>
              <Input type="number" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="100" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Unit Cost</Label>
              <Input type="number" value={formData.unit_cost} onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })} placeholder="0.00" />
            </div>
            <div className="space-y-2">
              <Label>Reorder Level</Label>
              <Input type="number" value={formData.min_quantity} onChange={(e) => setFormData({ ...formData, min_quantity: e.target.value })} placeholder="10" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Input value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} placeholder="Supplier name" />
            </div>
            <div className="space-y-2">
              <Label>Storage Location</Label>
              <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Where stored" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Optional description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Add Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Stock Transaction Dialog
const StockTransactionDialog = ({ open, onOpenChange, item, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    transaction_type: 'used', quantity: '', reason: ''
  });

  const handleSubmit = () => {
    if (!formData.quantity) {
      toast.error('Please enter quantity');
      return;
    }
    onSubmit({ ...formData, quantity: parseInt(formData.quantity) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record Stock Transaction</DialogTitle>
          <DialogDescription>
            {item?.name} - Current stock: {item?.quantity_available}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Transaction Type</Label>
            <Select value={formData.transaction_type} onValueChange={(v) => setFormData({ ...formData, transaction_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="used">Used / Distributed</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
                <SelectItem value="damaged">Damaged / Lost</SelectItem>
                <SelectItem value="restocked">Restocked</SelectItem>
                <SelectItem value="adjustment">Manual Adjustment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Quantity *</Label>
            <Input type="number" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="Enter quantity" />
          </div>
          <div className="space-y-2">
            <Label>Reason / Notes</Label>
            <Textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Why this transaction?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Income Categories
const INCOME_CATEGORIES = [
  { value: 'sponsorship', label: 'Sponsorship' },
  { value: 'donation', label: 'Donation' },
  { value: 'registration', label: 'Registration' },
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'grant', label: 'Grant' },
  { value: 'other', label: 'Other' },
];

// Income Dialog Component
const IncomeDialog = ({ open, onOpenChange, income, payers, onSubmit, isLoading }) => {
  const currencyCodes = useCurrencyCodes();
  const [formData, setFormData] = useState({
    name: '',
    category: 'donation',
    amount: '',
    payer_name: '',
    payer_email: '',
    payer_phone: '',
    payer_type: 'individual',
    date_received: new Date().toISOString().split('T')[0],
    receipt_number: '',
    payment_method: 'cash',
    reference_number: '',
    notes: '',
    currency: 'USD',
  });

  React.useEffect(() => {
    if (income) {
      setFormData({
        name: income.name || '',
        category: income.category || 'donation',
        amount: income.amount?.toString() || '',
        payer_name: income.payer_name || '',
        payer_email: income.payer_email || '',
        payer_phone: income.payer_phone || '',
        payer_type: income.payer_type || 'individual',
        date_received: income.date_received?.split('T')[0] || new Date().toISOString().split('T')[0],
        receipt_number: income.receipt_number || '',
        payment_method: income.payment_method || 'cash',
        reference_number: income.reference_number || '',
        notes: income.notes || '',
        currency: (income.currency || 'USD').toUpperCase(),
      });
    } else {
      setFormData({
        name: '',
        category: 'donation',
        amount: '',
        payer_name: '',
        payer_email: '',
        payer_phone: '',
        payer_type: 'individual',
        date_received: new Date().toISOString().split('T')[0],
        receipt_number: '',
        payment_method: 'cash',
        reference_number: '',
        notes: '',
        currency: 'USD',
      });
    }
  }, [income, open]);

  const handleSubmit = () => {
    if (!formData.name || !formData.amount || !formData.payer_name) {
      toast.error('Please fill in required fields (Name, Amount, Payer)');
      return;
    }
    onSubmit({ ...formData, amount: parseFloat(formData.amount) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{income ? 'Edit Income Entry' : 'Record Income / Donation'}</DialogTitle>
          <DialogDescription>Record a payment, donation, or other income</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                placeholder="e.g., Monthly Donation, Sponsorship" 
              />
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INCOME_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input 
                type="number" 
                step="0.01"
                value={formData.amount} 
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })} 
                placeholder="0.00" 
                data-testid="income-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Currency *</Label>
              <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                <SelectTrigger data-testid="income-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {currencyCodes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date Received *</Label>
              <Input 
                type="date" 
                value={formData.date_received} 
                onChange={(e) => setFormData({ ...formData, date_received: e.target.value })} 
              />
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Payer Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payer Name *</Label>
                <Input 
                  value={formData.payer_name} 
                  onChange={(e) => setFormData({ ...formData, payer_name: e.target.value })} 
                  placeholder="Person or company name"
                  list="payer-suggestions"
                />
                <datalist id="payer-suggestions">
                  {payers?.map(p => <option key={p.id} value={p.name} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label>Payer Type</Label>
                <Select value={['individual', 'company', 'organization'].includes(formData.payer_type) ? formData.payer_type : 'custom'} onValueChange={(v) => setFormData({ ...formData, payer_type: v, custom_payer_type: v === 'custom' ? (formData.custom_payer_type || '') : '' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="organization">Organization</SelectItem>
                    <SelectItem value="custom">Custom Type...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!['individual', 'company', 'organization'].includes(formData.payer_type) && (
              <div className="space-y-2 mt-3">
                <Label>Custom Payer Type</Label>
                <Input 
                  value={formData.custom_payer_type || (formData.payer_type !== 'custom' ? formData.payer_type : '')} 
                  onChange={(e) => setFormData({ ...formData, payer_type: 'custom', custom_payer_type: e.target.value })} 
                  placeholder="e.g., Sponsor, Foundation, Grant..." 
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="space-y-2">
                <Label>Payer Email</Label>
                <Input 
                  type="email"
                  value={formData.payer_email} 
                  onChange={(e) => setFormData({ ...formData, payer_email: e.target.value })} 
                  placeholder="email@example.com" 
                />
              </div>
              <div className="space-y-2">
                <Label>Payer Phone</Label>
                <Input 
                  value={formData.payer_phone} 
                  onChange={(e) => setFormData({ ...formData, payer_phone: e.target.value })} 
                  placeholder="+1234567890" 
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Payment Details</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="online">Online Payment</SelectItem>
                    <SelectItem value="mobile">Mobile Payment</SelectItem>
                    <SelectItem value="custom">Custom/Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Receipt Number</Label>
                <Input 
                  value={formData.receipt_number} 
                  onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })} 
                  placeholder="REC-001" 
                />
              </div>
              <div className="space-y-2">
                <Label>Reference/Transaction #</Label>
                <Input 
                  value={formData.reference_number} 
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} 
                  placeholder="Bank ref or txn ID" 
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea 
              value={formData.notes} 
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
              placeholder="Additional details about this income..." 
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {income ? 'Update' : 'Record Income'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Payer Dialog Component
const PayerDialog = ({ open, onOpenChange, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    payer_type: 'individual',
    custom_type: '',
    email: '',
    phone: '',
    address: '',
    contact_person: '',
    notes: '',
  });

  const handleSubmit = () => {
    if (!formData.name) {
      toast.error('Please enter a name');
      return;
    }
    // Map frontend fields to backend PayerCreate model
    const submitData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      company: formData.contact_person || formData.address || '',
      notes: formData.notes,
    };
    // Backend expects 'type' not 'payer_type'
    if (formData.payer_type === 'custom' && formData.custom_type) {
      submitData.type = formData.custom_type;
    } else {
      submitData.type = formData.payer_type;
    }
    onSubmit(submitData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Payer / Donor</DialogTitle>
          <DialogDescription>Create a new payer record for tracking payments</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                placeholder="Name or company" 
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.payer_type === 'individual' || formData.payer_type === 'company' || formData.payer_type === 'organization' ? formData.payer_type : 'custom'} onValueChange={(v) => setFormData({ ...formData, payer_type: v, custom_type: v === 'custom' ? formData.custom_type : '' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="organization">Organization</SelectItem>
                  <SelectItem value="custom">Custom Type...</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {formData.payer_type === 'custom' && (
            <div className="space-y-2">
              <Label>Custom Type *</Label>
              <Input 
                value={formData.custom_type} 
                onChange={(e) => setFormData({ ...formData, custom_type: e.target.value })} 
                placeholder="e.g., Sponsor, Grant Provider, Foundation..." 
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1234567890" />
            </div>
          </div>
          {formData.payer_type !== 'individual' && formData.payer_type !== 'custom' && (
            <div className="space-y-2">
              <Label>Contact Person</Label>
              <Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} placeholder="Primary contact" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Street address" />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isLoading || (formData.payer_type === 'custom' && !formData.custom_type)}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Add Payer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Payer Details Dialog with Payment History
const PayerDetailsDialog = ({ open, onOpenChange, payer, eventId }) => {
  const { data: payerDetails } = useQuery({
    queryKey: ['payer-details', eventId, payer?.id],
    queryFn: () => financeAPI.getPayer(eventId, payer.id).then(r => r.data),
    enabled: !!payer?.id && !!eventId,
  });

  const formatCurrency = (amount, cur) => fmtCurrency(amount, cur || 'USD');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            {payer?.name}
          </DialogTitle>
          <DialogDescription>
            {payer?.type === 'company' ? 'Company' : payer?.type === 'organization' ? 'Organization' : payer?.type === 'individual' ? 'Individual' : payer?.type || 'Individual'} • 
            Total: {formatCurrency(payer?.total_paid)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contact Info */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium mb-2">Contact Information</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {payer?.email && <div><span className="text-slate-500">Email:</span> {payer.email}</div>}
              {payer?.phone && <div><span className="text-slate-500">Phone:</span> {payer.phone}</div>}
              {payer?.address && <div className="col-span-2"><span className="text-slate-500">Address:</span> {payer.address}</div>}
              {payer?.contact_person && <div><span className="text-slate-500">Contact:</span> {payer.contact_person}</div>}
            </div>
          </div>

          {/* Payment History */}
          <div>
            <h4 className="font-medium mb-3">Payment History</h4>
            {payerDetails?.payments?.length === 0 ? (
              <p className="text-slate-500 text-sm">No payments recorded</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {payerDetails?.payments?.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-white border rounded">
                    <div>
                      <p className="font-medium">{payment.name}</p>
                      <div className="text-sm text-slate-500">
                        {payment.date_received && new Date(payment.date_received).toLocaleDateString()}
                        {payment.receipt_number && <span className="ml-2">• #{payment.receipt_number}</span>}
                      </div>
                    </div>
                    <p className="font-semibold text-green-600">{formatCurrency(payment.amount)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Total Payments:</span>
              <span className="text-xl font-bold text-green-600">{formatCurrency(payer?.total_paid)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FinanceManager;
